import { useState, useRef, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  IoDocumentTextOutline,
  IoCloudUploadOutline,
  IoDownloadOutline,
  IoTrashOutline,
  IoEyeOutline,
  IoSearchOutline,
  IoImageOutline,
  IoGridOutline,
  IoCodeSlashOutline,
  IoCloseOutline,
  IoChevronBackOutline,
  IoFolderOutline,
} from 'react-icons/io5'
import toast from 'react-hot-toast'
import { useFiles, useCreateFile, useDeleteFile, useDownloadFile } from '../hooks/useFiles'
import { usePatients } from '../hooks/usePatients'
import { LoadingSpinner } from '../components/ui/LoadingSpinner'
import type { FileMetadata } from '../types'

const formatDate = (d: string) => new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
const formatSize = (b: number) => {
  if (b < 1024) return `${b} B`
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`
  return `${(b / 1024 / 1024).toFixed(2)} MB`
}

function FileTypeIcon({ mimeType, name }: { mimeType?: string | null; name: string }) {
  const ext = name.split('.').pop()?.toLowerCase()
  const isImage = mimeType?.startsWith('image/') ?? ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext ?? '')
  const isSheet = mimeType?.includes('sheet') || ['xls', 'xlsx', 'csv'].includes(ext ?? '')
  const isCode = ['json', 'xml', 'txt'].includes(ext ?? '')
  if (isImage) return <IoImageOutline className='text-emerald-400 text-3xl' />
  if (isSheet) return <IoGridOutline className='text-green-400 text-3xl' />
  if (isCode) return <IoCodeSlashOutline className='text-blue-300 text-3xl' />
  return <IoDocumentTextOutline className='text-blue-400 text-3xl' />
}

function PdfViewer({ dataUrl, name, onClose }: { dataUrl: string; name: string; onClose: () => void }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className='fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex flex-col'>
      <div className='flex items-center justify-between px-5 py-3 bg-gray-900 border-b border-gray-700 flex-shrink-0'>
        <button onClick={onClose} className='flex items-center gap-2 text-gray-300 hover:text-white text-sm transition-colors'>
          <IoChevronBackOutline /> Retour
        </button>
        <span className='text-white font-medium text-sm truncate max-w-sm'>{name}</span>
        <a href={dataUrl} download={name}
          className='flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-xs font-semibold transition-colors'>
          <IoDownloadOutline /> Télécharger
        </a>
      </div>
      <iframe src={dataUrl} title={name} className='flex-1 border-0 bg-white' />
    </motion.div>
  )
}

function ImageViewer({ dataUrl, name, onClose }: { dataUrl: string; name: string; onClose: () => void }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className='fixed inset-0 bg-black/95 z-50 flex flex-col items-center justify-center'
      onClick={onClose}>
      <button onClick={onClose} className='absolute top-4 right-4 p-2 rounded-xl bg-white/10 hover:bg-white/20 text-white'>
        <IoCloseOutline size={20} />
      </button>
      <img src={dataUrl} alt={name} className='max-w-full max-h-[80vh] object-contain rounded-xl shadow-2xl'
        onClick={e => e.stopPropagation()} />
      <p className='text-white/50 text-sm mt-4'>{name}</p>
    </motion.div>
  )
}

export default function DocumentsPage() {
  const [searchQuery, setSearchQuery] = useState('')
  const [filterType, setFilterType] = useState<string>('all')
  const [isUploading, setIsUploading] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [preview, setPreview] = useState<{ dataUrl: string; name: string; type: 'pdf' | 'image' } | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { data: filesData, isLoading } = useFiles({ limit: 200 })
  const { data: patientsData } = usePatients({ limit: 500 })
  const createFile = useCreateFile()
  const deleteFile = useDeleteFile()
  const downloadFile = useDownloadFile()

  const allFiles = filesData?.data ?? []
  const patients = patientsData?.data ?? []

  const filteredFiles = allFiles.filter(f => {
    const q = searchQuery.toLowerCase()
    const matchName = f.name.toLowerCase().includes(q)
    const patient = patients.find(p => p.id === f.patientId)
    const matchPatient = patient ? `${patient.firstName} ${patient.lastName}`.toLowerCase().includes(q) : false
    const matchSearch = matchName || matchPatient
    if (!matchSearch) return false
    if (filterType === 'all') return true
    if (filterType === 'pdf') return f.mimeType === 'application/pdf' || f.name.endsWith('.pdf')
    if (filterType === 'image') return f.mimeType?.startsWith('image/') ?? false
    if (filterType === 'doc') return f.mimeType?.includes('word') || ['doc', 'docx'].includes(f.name.split('.').pop()?.toLowerCase() ?? '')
    return true
  })

  const processFile = useCallback(async (file: File, patientId?: number) => {
    if (file.size > 50 * 1024 * 1024) { toast.error('Fichier > 50 MB'); return }
    setIsUploading(true)
    try {
      const reader = new FileReader()
      const dataUrl = await new Promise<string>((res, rej) => {
        reader.onload = () => res(reader.result as string)
        reader.onerror = rej
        reader.readAsDataURL(file)
      })
      await createFile.mutateAsync({
        name: file.name,
        type: file.name.split('.').pop()?.toUpperCase() ?? 'FILE',
        mimeType: file.type,
        data: dataUrl.split(',')[1] ?? dataUrl,
        size: file.size,
        patientId,
      })
    } catch { toast.error("Erreur d'upload") }
    finally { setIsUploading(false); if (fileInputRef.current) fileInputRef.current.value = '' }
  }, [createFile])

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault(); setIsDragging(false)
    const f = e.dataTransfer.files[0]; if (f) void processFile(f)
  }

  const handlePreview = async (file: FileMetadata) => {
    try {
      const blob = await downloadFile.mutateAsync(file.id)
      const mime = file.mimeType ?? blob.type
      const url = URL.createObjectURL(blob)
      if (mime === 'application/pdf') setPreview({ dataUrl: url, name: file.name, type: 'pdf' })
      else if (mime.startsWith('image/')) setPreview({ dataUrl: url, name: file.name, type: 'image' })
      else { const a = document.createElement('a'); a.href = url; a.download = file.name; a.click(); URL.revokeObjectURL(url) }
    } catch { toast.error("Erreur de chargement") }
  }

  const handleDownload = async (file: FileMetadata) => {
    try {
      const blob = await downloadFile.mutateAsync(file.id)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a'); a.href = url; a.download = file.name; a.click()
      URL.revokeObjectURL(url)
    } catch { toast.error("Erreur de téléchargement") }
  }

  const handleDelete = async (file: FileMetadata) => {
    if (!window.confirm(`Supprimer "${file.name}" ?`)) return
    await deleteFile.mutateAsync(file.id)
  }

  const canPreview = (f: FileMetadata) => {
    const ext = f.name.split('.').pop()?.toLowerCase()
    return f.mimeType === 'application/pdf' || f.mimeType?.startsWith('image/') ||
      ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext ?? '')
  }

  const totalSize = allFiles.reduce((s, f) => s + f.size, 0)
  const pdfCount = allFiles.filter(f => f.mimeType === 'application/pdf' || f.name.endsWith('.pdf')).length
  const imgCount = allFiles.filter(f => f.mimeType?.startsWith('image/')).length

  return (
    <div className='min-h-full bg-gray-50 dark:bg-dark-200'>
      <AnimatePresence>
        {preview?.type === 'pdf' && <PdfViewer dataUrl={preview.dataUrl} name={preview.name} onClose={() => setPreview(null)} />}
        {preview?.type === 'image' && <ImageViewer dataUrl={preview.dataUrl} name={preview.name} onClose={() => setPreview(null)} />}
      </AnimatePresence>

      <div className='max-w-6xl mx-auto p-4 lg:p-6'>

        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} className='flex items-start justify-between flex-wrap gap-3 mb-6'>
          <div className='flex items-center gap-3'>
            <div className='bg-blue-500/10 p-2.5 rounded-xl'>
              <IoFolderOutline className='text-blue-500 text-2xl' />
            </div>
            <div>
              <h1 className='text-xl font-bold text-gray-900 dark:text-white'>Dossier partagé</h1>
              <p className='text-sm text-gray-500 dark:text-gray-400 mt-0.5'>{allFiles.length} document{allFiles.length !== 1 ? 's' : ''} · {formatSize(totalSize)}</p>
            </div>
          </div>
          <button onClick={() => fileInputRef.current?.click()}
            className='flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-500 hover:bg-blue-600 text-white text-sm font-semibold transition-colors shadow-sm'>
            <IoCloudUploadOutline size={16} />Uploader
          </button>
        </motion.div>

        {/* Stats rapides */}
        <div className='grid grid-cols-3 gap-3 mb-5'>
          {[
            { label: 'Total', value: allFiles.length, color: 'primary' },
            { label: 'PDF', value: pdfCount, color: 'blue' },
            { label: 'Images', value: imgCount, color: 'emerald' },
          ].map(({ label, value, color }, i) => (
            <motion.div key={label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className='bg-white dark:bg-dark-50 rounded-2xl p-4 border border-gray-100 dark:border-gray-800 text-center shadow-sm'>
              <div className={`text-2xl font-bold ${color === 'blue' ? 'text-blue-600 dark:text-blue-400' : color === 'emerald' ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-900 dark:text-white'}`}>{value}</div>
              <div className='text-xs text-gray-500 dark:text-gray-400 mt-0.5'>{label}</div>
            </motion.div>
          ))}
        </div>

        {/* Zone upload drag & drop */}
        <input ref={fileInputRef} type='file' className='hidden' onChange={e => { const f = e.target.files?.[0]; if (f) void processFile(f) }}
          accept='application/pdf,image/*,.doc,.docx,.xls,.xlsx,.txt' />
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
          onDragOver={e => { e.preventDefault(); setIsDragging(true) }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`mb-5 rounded-2xl border-2 border-dashed p-6 text-center cursor-pointer transition-all ${
            isDragging ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/10' : 'border-gray-200 dark:border-gray-700 hover:border-blue-400'
          }`}>
          {isUploading ? (
            <div className='flex items-center justify-center gap-3'>
              <LoadingSpinner />
              <span className='text-sm text-gray-500'>Upload en cours…</span>
            </div>
          ) : (
            <div className='flex items-center justify-center gap-3'>
              <IoCloudUploadOutline className={`text-2xl ${isDragging ? 'text-blue-500' : 'text-gray-400'}`} />
              <span className='text-sm text-gray-500 dark:text-gray-400'>
                {isDragging ? 'Déposez le fichier ici' : 'Glissez-déposez un fichier ou cliquez pour uploader (max 50 MB)'}
              </span>
            </div>
          )}
        </motion.div>

        {/* Recherche + filtres */}
        <div className='flex gap-2 mb-4 flex-wrap'>
          <div className='relative flex-1 min-w-[180px]'>
            <IoSearchOutline className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm' />
            <input type='text' placeholder='Rechercher un document ou patient…'
              value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              className='w-full pl-9 pr-4 py-2.5 text-sm bg-white dark:bg-dark-50 border border-gray-200 dark:border-gray-700 rounded-xl outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 transition-all text-gray-900 dark:text-gray-100 placeholder-gray-400' />
          </div>
          <div className='flex bg-white dark:bg-dark-50 rounded-xl border border-gray-200 dark:border-gray-700 p-0.5 gap-0.5'>
            {(['all', 'pdf', 'image', 'doc'] as const).map(t => (
              <button key={t} onClick={() => setFilterType(t)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  filterType === t ? 'bg-blue-500 text-white' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                }`}>
                {t === 'all' ? 'Tout' : t === 'pdf' ? 'PDF' : t === 'image' ? 'Images' : 'Docs'}
              </button>
            ))}
          </div>
        </div>

        {/* Liste */}
        {isLoading ? (
          <div className='flex justify-center py-16'><LoadingSpinner size='lg' /></div>
        ) : filteredFiles.length === 0 ? (
          <div className='bg-white dark:bg-dark-50 rounded-2xl border border-gray-100 dark:border-gray-800 p-12 text-center'>
            <IoFolderOutline className='text-gray-300 dark:text-gray-600 text-5xl mx-auto mb-3' />
            <p className='text-gray-500 dark:text-gray-400'>{searchQuery ? 'Aucun résultat pour cette recherche' : 'Aucun document'}</p>
          </div>
        ) : (
          <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3'>
            {filteredFiles.map((file, i) => {
              const patient = patients.find(p => p.id === file.patientId)
              const isPDF = file.mimeType === 'application/pdf' || file.name.endsWith('.pdf')
              return (
                <motion.div key={file.id}
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                  className='bg-white dark:bg-dark-50 rounded-2xl border border-gray-100 dark:border-gray-800 p-4 shadow-sm hover:shadow-md hover:border-blue-200 dark:hover:border-blue-500/30 transition-all'>
                  <div className='flex items-start gap-3'>
                    <div className='bg-blue-500/10 p-2.5 rounded-xl flex-shrink-0'>
                      <FileTypeIcon mimeType={file.mimeType} name={file.name} />
                    </div>
                    <div className='flex-1 min-w-0'>
                      <p className='font-semibold text-gray-900 dark:text-white text-sm truncate'>{file.name}</p>
                      {patient && (
                        <p className='text-xs text-blue-600 dark:text-blue-400 mt-0.5 truncate'>
                          {patient.lastName.toUpperCase()} {patient.firstName}
                        </p>
                      )}
                      <div className='flex items-center gap-1.5 mt-1 text-xs text-gray-400 flex-wrap'>
                        <span>{formatSize(file.size)}</span>
                        <span>·</span>
                        <span>{formatDate(file.createdAt)}</span>
                        {isPDF && <span className='font-semibold text-blue-400'>PDF</span>}
                      </div>
                    </div>
                  </div>
                  <div className='flex gap-2 mt-3 pt-3 border-t border-gray-100 dark:border-gray-800'>
                    {canPreview(file) && (
                      <button onClick={() => void handlePreview(file)} disabled={downloadFile.isPending}
                        className='flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30 text-emerald-600 dark:text-emerald-400 text-xs font-medium hover:bg-emerald-100 transition-colors'>
                        <IoEyeOutline className='text-sm' />Aperçu
                      </button>
                    )}
                    <button onClick={() => void handleDownload(file)} disabled={downloadFile.isPending}
                      className='flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/30 text-blue-600 dark:text-blue-400 text-xs font-medium hover:bg-blue-100 transition-colors'>
                      <IoDownloadOutline className='text-sm' />
                      {downloadFile.isPending ? '…' : 'Télécharger'}
                    </button>
                    <button onClick={() => void handleDelete(file)} disabled={deleteFile.isPending}
                      className='p-1.5 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 text-red-400 hover:bg-red-100 transition-colors'>
                      <IoTrashOutline className='text-sm' />
                    </button>
                  </div>
                </motion.div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
